import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Upload, FileText, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PartnerOnboarding = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    address: '',
    pincode: '',
    state: '',
    country: 'India',
    mobile: '',
    panNumber: '',
    aadharNumber: ''
  });

  const [files, setFiles] = useState({
    addressProof: null,
    panCard: null,
    aadharCard: null
  });

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Pincode validation - only numbers, max 6 digits
    if (name === 'pincode') {
      const numericValue = value.replace(/\D/g, '').slice(0, 6);
      setFormData({ ...formData, [name]: numericValue });
      return;
    }
    
    // Mobile number validation - only numbers, max 10 digits
    if (name === 'mobile') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData({ ...formData, [name]: numericValue });
      return;
    }
    
    // Aadhar number validation - only numbers, max 12 digits
    if (name === 'aadharNumber') {
      const numericValue = value.replace(/\D/g, '').slice(0, 12);
      // Format with spaces: XXXX XXXX XXXX
      const formatted = numericValue.replace(/(\d{4})(?=\d)/g, '$1 ');
      setFormData({ ...formData, [name]: formatted });
      return;
    }
    
    // PAN number validation - uppercase alphanumeric, max 10 chars
    if (name === 'panNumber') {
      const upperValue = value.toUpperCase().slice(0, 10);
      setFormData({ ...formData, [name]: upperValue });
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (fieldName, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload a file smaller than 5MB",
          variant: "destructive"
        });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a JPEG, PNG, or PDF file",
          variant: "destructive"
        });
        return;
      }
      
      setFiles({ ...files, [fieldName]: file });
      toast({
        title: "File uploaded",
        description: `${file.name} uploaded successfully`,
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.pincode.length !== 6) {
      toast({
        title: "Invalid Pincode",
        description: "Pincode must be exactly 6 digits",
        variant: "destructive"
      });
      return;
    }
    
    if (formData.mobile.length !== 10) {
      toast({
        title: "Invalid Mobile Number",
        description: "Mobile number must be exactly 10 digits",
        variant: "destructive"
      });
      return;
    }
    
    if (formData.aadharNumber.replace(/\s/g, '').length !== 12) {
      toast({
        title: "Invalid Aadhar Number",
        description: "Aadhar number must be exactly 12 digits",
        variant: "destructive"
      });
      return;
    }
    
    if (formData.panNumber.length !== 10) {
      toast({
        title: "Invalid PAN Number",
        description: "PAN number must be exactly 10 characters",
        variant: "destructive"
      });
      return;
    }
    
    console.log('Form Data:', formData);
    console.log('Files:', files);
    
    toast({
      title: "Application Submitted!",
      description: "Your partner onboarding application has been received. We'll contact you within 48 hours.",
    });
    
    // Reset form
    setFormData({
      firstName: '',
      middleName: '',
      lastName: '',
      address: '',
      pincode: '',
      state: '',
      country: 'India',
      mobile: '',
      panNumber: '',
      aadharNumber: ''
    });
    setFiles({
      addressProof: null,
      panCard: null,
      aadharCard: null
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Header />
      
      {/* Page Header */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Partner Onboarding
          </h1>
          <p className="text-xl text-gray-600">
            Complete the form below to become a Direct Selling Agent with Capital IQ
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <Card className="shadow-2xl border-gray-200">
            <CardHeader className="bg-gradient-to-r from-[#2C3E50] to-[#3d5568] text-white rounded-t-lg">
              <CardTitle className="text-3xl">Partner Registration Form</CardTitle>
              <CardDescription className="text-gray-200 text-base">
                Please fill in all required details. All fields marked with * are mandatory.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Personal Information */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#DC143C]">
                    Personal Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="firstName" className="text-base">First Name (as per Bank) *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="middleName" className="text-base">Middle Name</Label>
                      <Input
                        id="middleName"
                        name="middleName"
                        placeholder="Middle Name"
                        value={formData.middleName}
                        onChange={handleChange}
                        className="mt-2"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="lastName" className="text-base">Last Name (as per Bank) *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#DC143C]">
                    Address Details
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="address" className="text-base">Address *</Label>
                      <Textarea
                        id="address"
                        name="address"
                        placeholder="Enter complete address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        rows={3}
                        className="mt-2"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-base">Address Proof Upload *</Label>
                      <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-[#DC143C] transition-colors">
                        <input
                          type="file"
                          id="addressProof"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => handleFileChange('addressProof', e)}
                          className="hidden"
                        />
                        <label
                          htmlFor="addressProof"
                          className="flex flex-col items-center cursor-pointer"
                        >
                          {files.addressProof ? (
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-6 h-6" />
                              <span className="font-medium">{files.addressProof.name}</span>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 text-gray-400 mb-2" />
                              <span className="text-sm text-gray-600">Click to upload address proof</span>
                              <span className="text-xs text-gray-500 mt-1">(JPEG, PNG, or PDF, max 5MB)</span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <Label htmlFor="pincode" className="text-base">Pincode *</Label>
                        <Input
                          id="pincode"
                          name="pincode"
                          placeholder="6-digit pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          required
                          maxLength={6}
                          className="mt-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">{formData.pincode.length}/6 digits</p>
                      </div>
                      
                      <div>
                        <Label htmlFor="state" className="text-base">State *</Label>
                        <Select
                          value={formData.state}
                          onValueChange={(value) => handleSelectChange('state', value)}
                          required
                        >
                          <SelectTrigger id="state" className="mt-2">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {indianStates.map((state) => (
                              <SelectItem key={state} value={state}>
                                {state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="country" className="text-base">Country</Label>
                        <Input
                          id="country"
                          name="country"
                          value={formData.country}
                          disabled
                          className="mt-2 bg-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#DC143C]">
                    Contact Information
                  </h3>
                  
                  <div>
                    <Label htmlFor="mobile" className="text-base">Mobile Number *</Label>
                    <Input
                      id="mobile"
                      name="mobile"
                      placeholder="10-digit mobile number"
                      value={formData.mobile}
                      onChange={handleChange}
                      required
                      maxLength={10}
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.mobile.length}/10 digits</p>
                  </div>
                </div>

                {/* KYC Documents */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#DC143C]">
                    KYC Documents
                  </h3>
                  
                  <div className="space-y-6">
                    {/* PAN Card */}
                    <div>
                      <Label htmlFor="panNumber" className="text-base">PAN Number *</Label>
                      <Input
                        id="panNumber"
                        name="panNumber"
                        placeholder="ABCDE1234F"
                        value={formData.panNumber}
                        onChange={handleChange}
                        required
                        maxLength={10}
                        className="mt-2 uppercase"
                      />
                      <p className="text-xs text-gray-500 mt-1">{formData.panNumber.length}/10 characters</p>
                      
                      <div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-[#DC143C] transition-colors">
                        <input
                          type="file"
                          id="panCard"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => handleFileChange('panCard', e)}
                          className="hidden"
                        />
                        <label
                          htmlFor="panCard"
                          className="flex flex-col items-center cursor-pointer"
                        >
                          {files.panCard ? (
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-6 h-6" />
                              <span className="font-medium">{files.panCard.name}</span>
                            </div>
                          ) : (
                            <>
                              <FileText className="w-8 h-8 text-gray-400 mb-2" />
                              <span className="text-sm text-gray-600">Click to upload PAN Card</span>
                              <span className="text-xs text-gray-500 mt-1">(JPEG, PNG, or PDF, max 5MB)</span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                    
                    {/* Aadhar Card */}
                    <div>
                      <Label htmlFor="aadharNumber" className="text-base">Aadhar Number *</Label>
                      <Input
                        id="aadharNumber"
                        name="aadharNumber"
                        placeholder="XXXX XXXX XXXX"
                        value={formData.aadharNumber}
                        onChange={handleChange}
                        required
                        maxLength={14}
                        className="mt-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.aadharNumber.replace(/\s/g, '').length}/12 digits
                      </p>
                      
                      <div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-[#DC143C] transition-colors">
                        <input
                          type="file"
                          id="aadharCard"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => handleFileChange('aadharCard', e)}
                          className="hidden"
                        />
                        <label
                          htmlFor="aadharCard"
                          className="flex flex-col items-center cursor-pointer"
                        >
                          {files.aadharCard ? (
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-6 h-6" />
                              <span className="font-medium">{files.aadharCard.name}</span>
                            </div>
                          ) : (
                            <>
                              <FileText className="w-8 h-8 text-gray-400 mb-2" />
                              <span className="text-sm text-gray-600">Click to upload Aadhar Card</span>
                              <span className="text-xs text-gray-500 mt-1">(JPEG, PNG, or PDF, max 5MB)</span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Declaration */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    <strong>Declaration:</strong> I hereby declare that all the information provided above is true and correct to the best of my knowledge. I understand that any false information may lead to rejection of my application or termination of partnership.
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full bg-[#DC143C] hover:bg-[#B8112A] text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Submit Application
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnerOnboarding;
